import { Component, OnInit } from '@angular/core'
import { FormGroup, FormBuilder, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import {
  CodeLookup,
  CommentAPIModel,
  PaginatedAPI,
  QueriesAPIModel,
  StatusAPIModel,
  UserAPIModel
} from 'src/app/models/api.model'
import { QueryService } from 'src/app/services/query/query.service'
import {
  CommonService,
  formatDateToDDMMYYYY,
  formatDatetoYYYYMMDD,
  getOptionsValue
} from 'src/app/services/common/common.service'
import { DocumentService } from 'src/app/services/common/document.service'
import { OptionsService } from '../../../services/common/options.service'
import { Store } from '@ngrx/store'
import { AppState } from '../../../store/state/app.state'
import { map, Subscription } from 'rxjs'
import { getUserFromState } from '../../../store/selector/initialize.selector'
import { addFormControl } from '../../../utils/form-control.util'

@Component({
  selector: 'app-query-details',
  templateUrl: './query-details.component.html',
  styleUrls: ['./query-details.component.scss']
})
export class QueryDetailsComponent implements OnInit {
  queryDetailsForm: FormGroup
  query: QueriesAPIModel
  commentsToSave: CommentAPIModel[] = []
  private id: string
  readonly QUERY_SUMMARY_LINK = `/${GlobalConstants.ROUTING.QUERY}/${GlobalConstants.ROUTING.SUMMARY}`
  readonly MODULENAME = GlobalConstants.MODULE.QUERIES
  readonly userType = GlobalConstants.USER_TYPE
  readonly queryReminderOptions = GlobalConstants.queryReminderOptions
  readonly ASSIGNED_TO_CONTROL_NAME = 'assignedTo'
  readonly QUERY_CATEGORY_CONTROL_NAME = 'queryCategory'
  readonly QUERY_PRIORITY_CONTROL_NAME = 'priority'
  readonly EPFO_TIMELINE_CONTROL_NAME = 'epfoTimeline'
  readonly NOTES_CONTROL_NAME = 'notes'
  readonly QUERY_TAG_CONTROL_NAME = 'tag'
  readonly INTERNAL_DUE_DATE_CONTROL_NAME = 'nextInternalDueDate'
  readonly REMINDER_TYPE_CONTROL_NAME = 'reminderType'
  readonly TASK_STATUS_PENDING_WITH_EE = 'PENDING_WITH_EE'

  filesToBeUploaded: File[]
  statusValueChange: boolean = false
  isDisabled: boolean = false
  hasFormChanged: boolean
  notify: boolean
  queryCategories: CodeLookup[]
  queryTags: CodeLookup[]
  priorityLevels: CodeLookup[]
  user$: Subscription
  user: UserAPIModel
  isEpfoTimelineRequired: boolean
  employeeLinkedQueryArray: string[][] = []
  protected readonly getOptionsValue = getOptionsValue

  constructor(
    private queryService: QueryService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private cs: CommonService,
    private documentService: DocumentService,
    private optionsService: OptionsService,
    private appState: Store<AppState>
  ) {}

  addAssignedToForm = addFormControl(this.ASSIGNED_TO_CONTROL_NAME, '')
  addQueryCategoryForm = addFormControl(this.QUERY_CATEGORY_CONTROL_NAME, '', [Validators.required])
  addQueryPriorityForm = addFormControl(this.QUERY_PRIORITY_CONTROL_NAME, '')
  addEpfoTimelineForm = addFormControl(this.EPFO_TIMELINE_CONTROL_NAME, '')
  addNotesForm = addFormControl(this.NOTES_CONTROL_NAME, '')
  addTagForm = addFormControl(this.QUERY_TAG_CONTROL_NAME, '')
  addInternalDueDateForm = addFormControl(this.INTERNAL_DUE_DATE_CONTROL_NAME, '')

  ngOnInit() {
    this.buildQueryDetailsForm()
    this.getUserDetailsFromStore()
    this.populateOptions()
    this.route.params.subscribe(params => {
      const id = params['id']
      this.id = id
      this.dispatchFetchQueryDetailsAction(id)
      this.getQueryDetailsFromStore()
    })
  }

  private buildQueryDetailsForm() {
    this.queryDetailsForm = this.formBuilder.group({
      displayId: [{ value: '', disabled: true }],
      title: [{ value: '', disabled: true }],
      type: [{ value: '', disabled: true }],
      createdDatetime: [{ value: '', disabled: true }],
      status: ['', [Validators.required]],
      detailedStatus: ['', []],
      description: [{ value: '', disabled: true }, []],
      statusUpdateFlag: [false, []],
      daysElapsed: [{ value: '', disabled: true }, []],
      ownerDisplayId: [{ value: '', disabled: true }, []],
      empName: [{ value: '', disabled: true }, []],
      empStatus: [{ value: '', disabled: true }, []],
      empCurrentPfNumber: [{ value: '', disabled: true }, []],
      empUanNumber: [{ value: '', disabled: true }, []],
      empDoj: [{ value: '', disabled: true }, []],
      notify: [false],
      nextAction: ['', []],
      reminderType: [false]
    })
  }

  get f() {
    return this.queryDetailsForm.controls
  }

  private dispatchFetchQueryDetailsAction(id: any) {
    this.queryService.dispatchFetchQueryDetailsAction(id)
  }

  private getQueryDetailsFromStore() {
    this.queryService.getQueryDetailsFromStore().subscribe(query => {
      if (query) {
        this.query = query
        let modifiedQuery: any = { ...this.query }

        if (!query.daysElapsed) {
          modifiedQuery = { ...modifiedQuery, daysElapsed: 0 }
        }

        if (query.nextInternalDueDate) {
          modifiedQuery = {
            ...modifiedQuery,
            nextInternalDueDate: formatDatetoYYYYMMDD(query.nextInternalDueDate)
          }
        }

        if (query.epfoTimeline) {
          modifiedQuery = {
            ...modifiedQuery,
            epfoTimeline: formatDatetoYYYYMMDD(query.epfoTimeline)
          }
        }

        const tagsArray = query.tag ? query.tag.split(',') : []

        modifiedQuery = {
          ...modifiedQuery,
          tag: tagsArray
        }

        this.query = { ...this.query, ...modifiedQuery }
        this.queryDetailsForm.patchValue(this.query)
        this.queryDetailsForm.controls['createdDatetime'].setValue(formatDateToDDMMYYYY(this.query.createdDatetime))
        this.queryDetailsForm.controls['empDoj']?.setValue(formatDateToDDMMYYYY(this.query.empDoj))
        this.updateFields()
        this.subscribeFormForValueChanges(this.queryDetailsForm)
      }
    })
  }

  addComment(commentAPIModel: CommentAPIModel) {
    this.commentsToSave = [commentAPIModel]
  }

  getIfNotifySelected(notify) {
    this.notify = notify || false
  }

  updateQuery() {
    if (this.queryDetailsForm.invalid) {
      return this.cs.showError('Invalid Input')
    }

    if (this.showReminderTypeMissingInfoMessage()) {
      this.cs.showInfo('Reminder type is not selected')
    }

    const commentToBeSaved = this.commentsToSave && this.commentsToSave[0]?.comment ? this.commentsToSave : []
    const notifyState = this.determineNotifyState(commentToBeSaved) || false

    const selectedTags = this.queryDetailsForm.value.tag
    const tagsAsString =
      Array.isArray(selectedTags) && selectedTags.length > 0
        ? selectedTags.map(u => (u.display ? u.display : u)).join(',')
        : ''

    const {
      reminderInitiatedDate,
      reminderTriggeredAt,
      reminderType,
      shouldUpdateReminderFields
    } = this.buildReminderFields()

    this.queryService.dispatchUpdateQueryAction(
      {
        ...this.queryDetailsForm.value,
        companyEntityId: this.query.companyEntityId,
        statusUpdateFlag: this.statusValueChange,
        comments: commentToBeSaved,
        title: this.query.title,
        notify: this.statusValueChange ? false : notifyState,
        tag: tagsAsString,
        reminderInitiatedDate,
        reminderTriggeredAt,
        reminderType,
        shouldUpdateReminderFields
      },
      this.id,
      this.filesToBeUploaded,
      this.QUERY_SUMMARY_LINK
    )
  }

  downloadQuery() {
    const endPoint = ServiceUrlConstants.createDownloadExportPDFEndpoint(GlobalConstants.MODULE.QUERIES, this.id)
    this.documentService.downloadDocument(endPoint)
  }

  onStatusSelected(status: StatusAPIModel) {
    this.queryDetailsForm.controls['status'].setValue(status.code)
    this.queryDetailsForm.controls['nextAction'].setValue(status.nextActionToBeTaken)
    this.updateEpfoTimelineRequired(status.code)
  }

  handleFileInput(files: File[]) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  onStatusChange(flag: boolean) {
    this.statusValueChange = flag
    this.isDisabled = flag
  }

  mandatoryflag(flag: boolean) {
    if (this.statusValueChange && flag) {
      this.isDisabled = true
    } else {
      this.isDisabled = false
    }
  }

  private subscribeFormForValueChanges(formGroup: FormGroup) {
    const initialValue = formGroup?.value
    formGroup.valueChanges.subscribe(value => {
      this.hasFormChanged = Object.keys(initialValue).some(
        key => formGroup.value[key] != initialValue[key] && formGroup.dirty
      )
    })
  }

  determineNotifyState(commentToBeSaved: CommentAPIModel[]) {
    const hasCommented = commentToBeSaved?.length > 0
    const hasUploadedFiles = this.filesToBeUploaded !== null && this.filesToBeUploaded?.length > 0
    return ((this.hasFormChanged != null && this.hasFormChanged) || hasCommented || hasUploadedFiles) && this.notify
  }

  handleAssignToSelectedValue(assignedTo: string) {
    this.queryDetailsForm.patchValue({ assignedTo })
  }

  private updateFields() {
    const query = this.query
    if (query && this.user?.userType) {
      if (this.user.userType === this.userType.RMS_ADMIN) {
        this.f?.[this.QUERY_CATEGORY_CONTROL_NAME]?.setValue(this.query?.queryCategory)
        this.f?.[this.QUERY_PRIORITY_CONTROL_NAME]?.setValue(this.query?.priority)
        this.f?.[this.EPFO_TIMELINE_CONTROL_NAME]?.setValue(this.query?.epfoTimeline)
        this.f?.[this.NOTES_CONTROL_NAME]?.setValue(this.query?.notes)
        this.f?.[this.QUERY_TAG_CONTROL_NAME]?.setValue(this.query?.tag)
        this.f?.[this.INTERNAL_DUE_DATE_CONTROL_NAME]?.setValue(this.query?.nextInternalDueDate)
      }
      this.f?.[this.ASSIGNED_TO_CONTROL_NAME]?.setValue(this.query?.assignedTo)
      this.updateEpfoTimelineRequired(this.queryDetailsForm.controls['status']?.value)
      this.markMandatoryFields()
      this.subscribeFormForValueChanges(this.queryDetailsForm)
    }
  }

  populateOptions() {
    let lookupValues: string[] = [
      GlobalConstants.OPTION_TYPE.QUERY_TAG,
      GlobalConstants.OPTION_TYPE.QUERY_CATEGORY,
      GlobalConstants.OPTION_TYPE.PRIORITY_LEVEL
    ]
    this.optionsService.getOptionsForType(lookupValues).subscribe({
      next: responses => {
        if (responses) {
          this.queryCategories = responses[GlobalConstants.OPTION_TYPE.QUERY_CATEGORY]
          const queryTagOptions = responses[GlobalConstants.OPTION_TYPE.QUERY_TAG]
          this.queryTags = queryTagOptions.map((queryTag: CodeLookup) => queryTag.key2)
          this.priorityLevels = responses[GlobalConstants.OPTION_TYPE.PRIORITY_LEVEL]
        }
      }
    })
  }

  getUserDetailsFromStore = () => {
    this.user$ = this.appState.select(getUserFromState).subscribe((user: UserAPIModel) => {
      if (user) {
        this.user = user
        this.addFormControls(user)
        this.updateFields()
      }
    })
  }

  private addFormControls(user) {
    if (user?.userType && user.userType === this.userType.RMS_ADMIN) {
      this.addQueryPriorityForm(this.queryDetailsForm)
      this.addQueryCategoryForm(this.queryDetailsForm)
      this.addEpfoTimelineForm(this.queryDetailsForm)
      this.addNotesForm(this.queryDetailsForm)
      this.addTagForm(this.queryDetailsForm)
      this.addInternalDueDateForm(this.queryDetailsForm)
    }
    this.addAssignedToForm(this.queryDetailsForm)
  }

  protected readonly formatDateToDDMMYYYY = formatDateToDDMMYYYY

  updateEpfoTimelineRequired(status: string) {
    let epfoTimelineCtrl
    this.isEpfoTimelineRequired = status === 'PENDING_WITH_EPFO'
    epfoTimelineCtrl = this.queryDetailsForm?.get(this.EPFO_TIMELINE_CONTROL_NAME)
    if (this.isEpfoTimelineRequired) {
      epfoTimelineCtrl?.setValidators([Validators.required])
    } else {
      epfoTimelineCtrl?.clearValidators()
    }
    epfoTimelineCtrl?.updateValueAndValidity()
    this.markMandatoryFields()
  }

  markMandatoryFields() {
    const queryCategoryValue = this.queryDetailsForm?.get(this.QUERY_CATEGORY_CONTROL_NAME)?.value

    if (!queryCategoryValue) {
      this.queryDetailsForm.get(this.QUERY_CATEGORY_CONTROL_NAME)?.markAsTouched()
    }

    const epfoTimelineValue = this.queryDetailsForm?.get(this.EPFO_TIMELINE_CONTROL_NAME)?.value

    if (this.isEpfoTimelineRequired && !epfoTimelineValue) {
      this.queryDetailsForm.get(this.EPFO_TIMELINE_CONTROL_NAME)?.markAsTouched()
    }
  }

  fetchEmployeeLinkedQueryDetails() {
    if (this.employeeLinkedQueryArray.length > 0) {
      return
    }

    const params = [
      { key: 'pageNo', value: '0' },
      { key: 'limit', value: '1000000' },
      { key: 'sortBy', value: 'createdDatetime' },
      { key: 'sortOrder', value: 'DESC' }
    ]
    const ownerId: string = this.query?.ownerId

    this.queryService.invokeQuerySummaryAPI(params, { ownerId })
      .pipe(map((paginatedQuerySummary: PaginatedAPI<QueriesAPIModel>) => paginatedQuerySummary))
      .subscribe(paginatedQuerySummary => {
        const queryList: QueriesAPIModel[] = paginatedQuerySummary?.content
        if (queryList) {
          this.employeeLinkedQueryArray = queryList
            .filter(queries => queries.id !== this.id)
            .map(queries => [queries.id, queries.displayId, queries.status, queries.description,
              getOptionsValue(this.queryCategories, queries.queryCategory),
              formatDateToDDMMYYYY(queries.createdDatetime)]
            ) // 0 - Query ID, 1 - Query Display ID, 2 - Query status, 3 - Description, 4 - Category of Query, 5- Query Raised On
        }
      })
  }

  getQueryDetailsUrl(queryId: string): string {
    return `/query/${queryId}/details`
  }

  reminderTypeUpdated(): boolean {
    const existingType = this.query?.reminderType
    const updatedType = this.queryDetailsForm?.get(this.REMINDER_TYPE_CONTROL_NAME)?.value
    return (
      (existingType && updatedType !== existingType) ||
      (!existingType && updatedType)
    )
  }

  buildReminderFields(): {
    reminderInitiatedDate: string | null;
    reminderTriggeredAt: string | null;
    reminderType: string;
    shouldUpdateReminderFields: boolean;
  } {
    const isReminderTypeUpdated = this.reminderTypeUpdated()
    const reminderTypeControl = this.queryDetailsForm?.get(this.REMINDER_TYPE_CONTROL_NAME)
    let reminderInitiatedDate = this.query?.reminderInitiatedDate
    let reminderTriggeredAt = this.query?.reminderTriggeredAt
    let reminderType = reminderTypeControl?.value || this.query.reminderType
    let shouldUpdateReminderFields = true

    if (this.statusValueChange && this.queryDetailsForm?.value?.status !== this.TASK_STATUS_PENDING_WITH_EE) {
      reminderInitiatedDate = null
      reminderTriggeredAt = null
      reminderType = null
    } else if (isReminderTypeUpdated) {
      reminderInitiatedDate = new Date().toISOString()
      reminderTriggeredAt = null
    }

    return {
      reminderInitiatedDate,
      reminderTriggeredAt,
      reminderType,
      shouldUpdateReminderFields
    }
  }

  showReminderTypeMissingInfoMessage(): boolean {
    const queryFormVal = this.queryDetailsForm?.value
    return queryFormVal?.status === this.TASK_STATUS_PENDING_WITH_EE && !queryFormVal?.reminderType
  }

  ngOnDestroy() {
    this.user$?.unsubscribe()
  }
}
